import "./DocumentationSample.scss";
import { useCallback, useState } from "react";
import { Button, Card, CardContent, IconButton, Tooltip } from "../../../shared/components";
import { JsxRenderer } from "../ContentRenderer/JsxRenderer";
import CodeMirror, { type BasicSetupOptions } from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { indentUnit } from "@codemirror/language";
import { EditorView } from "@codemirror/view";
import { eagerParsing } from "../../../shared/utils/codemirror";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CopyAll from "@mui/icons-material/CopyAll";
import RestoreOutlined from "@mui/icons-material/RestoreOutlined";
import { getDataProps } from "../../../shared/utilities";
import * as _ from "lodash";

type DocumentationSampleProps = {
    name?: string;
    input: string;
};

function trimExcessIndentation(input: string) {
    const leadingTabs = /^(\t*)/gm;
    const leadingSpaces = /^([ ]*)/gm;
    const blankLines = /^\s*\n/gm;
    const content = input.replaceAll(blankLines, "");

    const minLeadingTabs = Array.from(content.matchAll(leadingTabs)).map(m => m[1].length).reduce((a, b) => Math.min(a, b), Infinity);
    const minLeadingSpaces = Array.from(content.matchAll(leadingSpaces)).map(m => m[1].length).reduce((a, b) => Math.min(a, b), Infinity);

    if (minLeadingTabs > 0) {
        return input.replace(new RegExp(`^\\t{${minLeadingTabs}}`, "gm"), "").trim();
    }
    else if (minLeadingSpaces > 0) {
        return input.replace(new RegExp(`^[ ]{${minLeadingSpaces}}`, "gm"), "").trim();
    }

    return input.trim();
}

// Extensions/setup must be referentially stable — @uiw/react-codemirror reconfigures the editor
// whenever these props change identity...
const editorExtensions = [javascript({ jsx: true }), indentUnit.of("    "), eagerParsing];
const editorSetup: BasicSetupOptions = { lineNumbers: true, foldGutter: true };

const summaryOpeningTag = "\\n(?:\\s*)<!-- SUMMARY START -->(?:\\s*)\\n";
const summaryClosingTag = "\\n(?:\\s*)<!-- SUMMARY END -->(?:\\s*)\\n";
const summaryMatchRegex = new RegExp(`([\\S\\s]*)${summaryOpeningTag}([\\S\\s]*)${summaryClosingTag}([\\S\\s]*)`);

function DocumentationSample(props: DocumentationSampleProps) {
    const { input } = props;

    // Use Regex to extract the various parts of the source input...
    const summaryMatches = input.match(summaryMatchRegex);
    const containsSummary = !!summaryMatches;
    const prefix = summaryMatches?.[1] ?? "";
    const summary = summaryMatches?.[2] ?? input.trim();
    const suffix = summaryMatches?.[3] ?? "";

    // Reconstitute the original source code from the various parts (stripping out markers)...
    const fullSource = [prefix, summary, suffix].filter(s => s !== "").join("\n");

    const [editMode, setEditMode] = useState("summary");
    const [content, setContent] = useState(fullSource);
    const [editor, setEditor] = useState<EditorView>(null);
    const [copyIcon, setCopyIcon] = useState(<CopyAll />);

    const source = trimExcessIndentation(editMode === "summary" ? summary : fullSource);

    const switchEditMode = useCallback(() => {
        setEditMode(editMode === "summary" ? "full" : "summary");
    }, [editMode, editor]);

    const onSourceChange = useCallback(_.debounce((newSource: string) => {
        setContent(
            editMode === "summary" ?
                [prefix, newSource, suffix].filter(s => s !== "").join("\n") :
                newSource
        );
    }, 500), [setContent, editMode, prefix, suffix]);

    const copySource = useCallback(() => {
        navigator.clipboard.writeText(editor.state.doc.toString());
        setCopyIcon(<CheckCircle color="success" />);
        setTimeout(() => {
            setCopyIcon(<CopyAll />);
        }, 1000);
    }, [editor]);

    const resetSource = useCallback(() => {
        editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: source } });
    }, [editor, source]);

    const editorDidMount = useCallback((view: EditorView) => {
        setEditor(view);
        setTimeout(() => view.dispatch({ selection: { anchor: 0 }, scrollIntoView: true }), 50);
    }, [setEditor]);

    return (
        <Card variant="outlined" className="DocumentationSample" {...getDataProps(props)}>
            <CardContent className="content">
                <JsxRenderer template={content} />
            </CardContent>
            <CardContent className="actions">
                <Tooltip title="Reset Code Changes" arrow>
                    <IconButton onClick={resetSource}><RestoreOutlined /></IconButton>
                </Tooltip>

                {
                    containsSummary && <Button variant="outlined" size="small" onClick={switchEditMode}>
                        {editMode === "summary" ? "Expand Code" : "Collapse Code"}
                    </Button>
                }

                <Tooltip title="Copy Source Code" arrow>
                    <IconButton onClick={copySource}>{copyIcon}</IconButton>
                </Tooltip>
            </CardContent>
            <CardContent className="editor">
                <CodeMirror
                    value={source}
                    theme={vscodeDark}
                    extensions={editorExtensions}
                    basicSetup={editorSetup}
                    onChange={onSourceChange}
                    onCreateEditor={editorDidMount}
                />
            </CardContent>
        </Card>
    );
}

export { DocumentationSample };